import { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { supabase } from '@/integrations/supabase/client'
import { Label } from '@/components/ui/label'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Trophy, Users, DollarSign, Target } from 'lucide-react'
import { useToast } from '@/hooks/use-toast'

interface Wager {
  id: string
  challenger_id: string | null
  challenged_id: string | null
  brocode_challenger: string | null
  brocode_challenged: string | null
  twitter_challenger: string | null
  twitter_challenged: string | null
  description: string
  stake_top1: number | null
  stake_xp: number | null
  terms: string | null
  donation_percentage: number | null
  created_at: string | null
  status: string | null
  twitter_posted: boolean | null
  winning_user_id: string | null
  stake_currency: string | null
  escrow_required: boolean | null
  escrow_transaction_id: string | null
}

interface WagerBoardProps {
  userId: string
  currentUser?: any
}

const WagerBoard = ({ userId, currentUser }: WagerBoardProps) => {
  const [wagers, setWagers] = useState<Wager[]>([])
  const [userWagers, setUserWagers] = useState<Wager[]>([])
  const [activeTab, setActiveTab] = useState<'browse' | 'my-wagers'>('browse')
  const [loading, setLoading] = useState(false)
  
  const { toast } = useToast()

  useEffect(() => {
    loadWagers()
    loadUserWagers()
  }, [])

  const loadWagers = async () => {
    try {
      const { data, error } = await supabase
        .from('wagers')
        .select('*')
        .eq('status', 'open')
        .order('created_at', { ascending: false })
      
      if (error) throw error
      setWagers(data || [])
    } catch (err) {
      console.error('Failed to load wagers:', err)
    }
  }

  const loadUserWagers = async () => {
    try {
      const { data, error } = await supabase
        .from('wagers')
        .select('*')
        .or(`challenger_id.eq.${userId},challenged_id.eq.${userId}`)
        .order('created_at', { ascending: false })
      
      if (error) throw error
      setUserWagers(data || [])
    } catch (err) {
      console.error('Failed to load user wagers:', err)
    }
  }

  const handleAcceptWager = async (wagerId: string) => {
    setLoading(true)
    try {
      const { error } = await supabase
        .from('wagers')
        .update({ 
          challenged_id: userId,
          brocode_challenged: currentUser?.brocode,
          twitter_challenged: currentUser?.twitter_handle,
          status: 'active'
        })
        .eq('id', wagerId)
        .eq('status', 'open')

      if (error) throw error

      toast({
        title: "Wager Accepted",
        description: "You have successfully joined this wager!",
      })

      await loadWagers()
      await loadUserWagers()
    } catch (err: any) {
      toast({
        title: "Error",
        description: err.message || "Failed to accept wager",
        variant: "destructive",
      })
    } finally {
      setLoading(false)
    }
  }

  const handleResolveWager = async (wagerId: string, winnerId: string) => {
    setLoading(true)
    try {
      const { error } = await supabase
        .from('wagers')
        .update({ 
          status: 'completed',
          winning_user_id: winnerId
        })
        .eq('id', wagerId)

      if (error) throw error

      toast({
        title: "Wager Resolved",
        description: "The wager has been resolved successfully!",
      })

      await loadUserWagers()
    } catch (err: any) {
      toast({
        title: "Error", 
        description: err.message || "Failed to resolve wager",
        variant: "destructive",
      })
    } finally {
      setLoading(false)
    }
  }

  const getStatusColor = (status: string | null) => {
    switch (status) {
      case 'open': return 'bg-primary text-primary-foreground'
      case 'active': return 'bg-secondary text-secondary-foreground' 
      case 'completed': return 'bg-accent text-accent-foreground'
      case 'expired': return 'bg-destructive text-destructive-foreground'
      default: return 'bg-muted text-muted-foreground'
    }
  }

  const canUserParticipate = (wager: Wager) => {
    return wager.challenger_id !== userId && !wager.challenged_id && wager.status === 'open'
  }

  const getStakeDisplay = (wager: Wager) => {
    if (wager.stake_xp) return `${wager.stake_xp} BroScore`
    if (wager.stake_top1) return `${wager.stake_top1} TOP1`
    return 'No stake specified'
  }

  const WagerCard = ({ wager, showActions = true }: { wager: Wager; showActions?: boolean }) => (
    <Card key={wager.id} className="border-border/20 bg-card">
      <CardHeader className="pb-3">
        <div className="flex justify-between items-start">
          <CardTitle className="text-lg text-foreground">
            Challenge by {wager.brocode_challenger || 'Anonymous'}
          </CardTitle>
          <Badge className={getStatusColor(wager.status)}>
            {(wager.status || 'unknown').toUpperCase()}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-muted-foreground">{wager.description}</p>
        
        <div className="grid grid-cols-2 gap-4">
          <div className="flex items-center gap-2">
            <DollarSign className="h-4 w-4 text-foreground" />
            <span className="font-semibold text-foreground">
              {getStakeDisplay(wager)}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Target className="h-4 w-4 text-foreground" />
            <span className="text-sm text-muted-foreground">
              {wager.donation_percentage}% to pool
            </span>
          </div>
        </div>

        {wager.terms && (
          <div>
            <Label className="text-sm font-medium text-foreground">Terms:</Label>
            <p className="text-sm text-muted-foreground">{wager.terms}</p>
          </div>
        )}

        {wager.challenged_id && (
          <div className="flex items-center gap-2">
            <Users className="h-4 w-4 text-foreground" />
            <span className="text-sm text-muted-foreground">
              vs {wager.brocode_challenged || 'Opponent'}
            </span>
          </div>
        )}

        {showActions && (
          <div className="flex gap-2 pt-2">
            {wager.status === 'open' && canUserParticipate(wager) && (
                <Button 
                  onClick={() => handleAcceptWager(wager.id)}
                  disabled={loading}
                  className="flex-1"
                >
                  Accept Challenge
                </Button>
            )}

            {wager.status === 'active' && 
             wager.challenger_id === userId && (
              <div className="flex gap-2 flex-1">
                <Button 
                  size="sm"
                  onClick={() => handleResolveWager(wager.id, wager.challenger_id!)}
                  className="flex-1"
                >
                  I Won
                </Button>
                <Button 
                  size="sm"
                  variant="outline"
                  onClick={() => handleResolveWager(wager.id, wager.challenged_id!)}
                  className="flex-1"
                >
                  They Won
                </Button>
              </div>
            )}

            {wager.status === 'completed' && wager.winning_user_id && (
              <div className="flex items-center gap-2">
                <Trophy className="h-4 w-4 text-yellow-500" />
                <span className="text-sm text-muted-foreground">
                  Winner: {wager.winning_user_id === userId ? 'You' : 
                          wager.winning_user_id === wager.challenger_id ? 
                          wager.brocode_challenger : wager.brocode_challenged}
                </span>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  )

  return (
    <div className="container mx-auto p-6 bg-background min-h-screen">
      <div className="mb-6">
        <h1 className="text-3xl font-bold mb-2 text-foreground">Wager Board</h1>
        <p className="text-muted-foreground">
          Challenge other Bros or accept challenges to earn BroScore and TOP1 tokens
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as any)} className="space-y-6">
        <TabsList className="grid w-full grid-cols-2 bg-secondary border-border">
          <TabsTrigger value="browse" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
            Browse Challenges
          </TabsTrigger>
          <TabsTrigger value="my-wagers" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
            My Wagers
          </TabsTrigger>
        </TabsList>

        <TabsContent value="browse" className="space-y-4">
          {wagers.length === 0 ? (
            <Card className="border-border/20 bg-card">
              <CardContent className="text-center py-8">
                <p className="text-muted-foreground">No open challenges available</p>
                <p className="text-sm text-muted-foreground mt-2">Check back later or create your own!</p>
              </CardContent>
            </Card>
          ) : (
            wagers.map(wager => (
              <WagerCard key={wager.id} wager={wager} />
            ))
          )}
        </TabsContent>

        <TabsContent value="my-wagers" className="space-y-4">
          {userWagers.length === 0 ? (
            <Card className="border-border/20 bg-card">
              <CardContent className="text-center py-8">
                <p className="text-muted-foreground">You haven't participated in any wagers yet</p>
                <p className="text-sm text-muted-foreground mt-2">Browse open challenges to get started!</p>
              </CardContent>
            </Card>
          ) : (
            userWagers.map(wager => (
              <WagerCard key={wager.id} wager={wager} />
            ))
          )}
        </TabsContent>
      </Tabs>
    </div>
  )
}

export default WagerBoard